import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import OnDeleteIcon from '../../../icons/OnDeleteIcon';
import {
    clearAppointmentAuditData,
    clearNewAppointment,
    deleteParticipantCalendarAppointmentSlot,
    loadAppointmentAuditData,
    loadAppointmentSlot,
    setOpenAppointmentSchedulerPanel,
    setParticipantsCurrentAppointment,
    updateParticipantCalendarAppointmentSlot
} from '../../../store/calendarService';
import { selectUsersForNamesById } from '../../../store/userSelectors';
import { loadTeam, loadUsersMinimalDetails } from '../../../store/userService';
import { isTimePast, reverseFormatDate } from '../../../utils/dateFunctions';
import { getNameFromId } from '../../../utils/directusFunctions';
import { createHash } from '../../../utils/stringUtils';
import { hasRole } from '../../../utils/userRoles';
import AuditAppointmentModal from '../../auditing/auditAppointments/AuditAppointmentModal';
import Button from '../../formElements/Button';
import DateSelect from '../../formElements/DateSelect';
import StaticField from '../../formElements/StaticField';
import Switch from '../../formElements/Switch';
import TextInputField from '../../formElements/TextInputField';
import Panel from '../../ui/panel/Panel';
import AdviserSelect from '../components/AdviserSelect';
import AppointmentTypeSelect from '../components/AppointmentTypeSelect';
import LocationSelect from '../components/LocationSelect';
import ParticipantsSelect from '../components/ParticipantsSelect';
import StartAndEndTimesSelect from '../components/StartAndEndTimesSelect';
import StartAppointmentSlotsRows from '../components/StartAppointmentSlotsRows';
import {
    acceptedRoles,
    cancelledStatuses,
    canEditSlot,
    deleteRoles,
    group1,
    unresolvedStatuses
} from '../utils/calendarUtils';
import { validate } from '../validation/validateAppointmentSlotsEdit';
import { ADDITIONAL_DETAILS_LIMIT } from '../validation/validationFunctions';

import sidePanel from '../styles/schedulePanel.module.css';

const ParticipantAppointmentSlotsEdit = ({ currentEvent }) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const initialState = {
        id: '',
        typeId: '',
        userId: '',
        locationId: '',
        startTime: '08:00',
        endTime: '09:00',
        date: String(new Date().toISOString().split('T')[0]),
        serviceId: '',
        teamId: '',
        participantIds: [],
        numberOfSlots: 1,
        type: '',
        service: '',
        location: '',
        additionalDetails: ''
    };

    const [initialStateOnEntry, setInitialStateOnEntry] = useState({});
    const [newEntry, setNewEntry] = useState(initialState);
    const [errors, setErrors] = useState({});
    const [appointmentAdviser, setAppointmentAdviser] = useState({});
    const [selectedParticipants, setSelectedParticipants] = useState([]);
    const [statuses, setStatuses] = useState({});
    const [reservedAppointments, setReservedAppointments] = useState([]);
    const [isDisabledStartSlotButton, setIsDisabledStartSlotButton] = useState(false);
    const [isDisabledUpdateSlotButton, setIsDisabledUpdateSlotButton] = useState(false);
    const [isUpdateButtonDisabled, setIsUpdateButtonDisabled] = useState(false);
    const [areAnySlotsResolved, setAreAnySlotsResolved] = useState(false);
    const [isInPast, setIsInPast] = useState(false);
    const [isAuditable, setIsAuditable] = useState(false);
    const [showAuditModal, setShowAuditModal] = useState(false);
    const [auditLabel, setAuditLabel] = useState('');
    const [auditParticipantId, setAuditParticipantId] = useState('');
    const [auditModalHeadingLabel, setAuditModalHeadingLabel] = useState('');
    const [hash, setHash] = useState('01');
    const [hasParticipantAccess, setHasParticipantAccess] = useState({});

    // STORE STATE
    const loggedInUser = useSelector((state) => state.entities.userService.loggedInUser);
    const { roles } = loggedInUser;
    const users = useSelector(selectUsersForNamesById);
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const serviceDetailsById = useSelector(
        (state) => state.entities.directusService.serviceDetailsById
    );
    const allTeams = useSelector((state) => state.entities.userService.allTeams);
    const appointmentTypesByContractId = useSelector(
        (state) => state.entities.directusService.appointmentTypeDetailsByContractId
    );
    const appointment = useSelector(
        (state) => state.entities.calendarService.participantsCurrentAppointment
    );
    const openAppointmentSchedulerPanel = useSelector(
        (state) => state.entities.calendarService.openAppointmentSchedulerPanel
    );
    const errorMessage = useSelector((state) => state.entities.formsState.errorMessage);
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USEEFFECTS
    useEffect(() => {
        dispatch(loadAppointmentSlot(currentEvent.id));
    }, []);

    useEffect(() => {
        if (!('id' in appointment) || appointment.id !== currentEvent.id) return;

        if ('location' in appointment) {
            /* eslint-disable no-unused-vars */
            const { location, ...rest } = appointment;
            setInitialStateOnEntry(rest);
        }

        if (appointment?.teamId) {
            const team = allTeams.find((el) => el.id === appointment.teamId);
            if (!team) dispatch(loadTeam(appointment.teamId));
        }

        if (appointment.participantIds?.length > 0) {
            const selected = appointment.participantIds.map((el) =>
                currentParticipant.id === el
                    ? currentParticipant
                    : { id: el, hidden: 'slot filled', contractId: currentParticipant.contractId }
            );
            setSelectedParticipants(selected);
            setHasParticipantAccess({ [currentParticipant.id]: true });
        }
    }, [appointment]);

    useEffect(() => {
        if (!(appointment.userId in users))
            dispatch(loadUsersMinimalDetails([currentEvent.userId]));
        else setAppointmentAdviser(users[currentEvent.userId]);
    }, [users]);

    useEffect(() => {
        if (
            !('id' in appointment) ||
            appointment.id !== currentEvent.id ||
            !(appointment?.serviceId in serviceDetailsById)
        )
            return;
        setNewEntry({
            ...appointment,
            service: serviceDetailsById[appointment.serviceId].name
        });
        setIsInPast(isTimePast(`${appointment.date}T${appointment.startTime}`));
        setIsDisabledStartSlotButton(!hasRole(acceptedRoles, roles));
        setIsDisabledUpdateSlotButton(!hasRole(group1, roles));
    }, [serviceDetailsById, appointment]);

    useEffect(() => {
        if (
            selectedParticipants.length < 1 ||
            !('id' in appointment) ||
            appointment.id !== currentEvent.id
        )
            return;
        const statuses = selectedParticipants.reduce(
            (acc, cur) => ({
                ...acc,
                [cur.id]:
                    appointment?.attendances.find((entry) => entry.participantId === cur.id)
                        ?.status || 'Not known'
            }),
            {}
        );
        setStatuses(statuses);
        setReservedAppointments(
            Object.values(statuses).filter((el) => !cancelledStatuses.includes(el))
        );
        setAreAnySlotsResolved(
            selectedParticipants.some((el) => !unresolvedStatuses.includes(statuses[el.id]))
        );
    }, [selectedParticipants, appointment]);

    useEffect(() => {
        if (!('id' in appointment) || appointment.id !== currentEvent.id) return;
        setNewEntry((prev) => ({
            ...prev,
            ...appointment
        }));
        setIsDisabledStartSlotButton(!hasRole(acceptedRoles, roles));
        setIsDisabledUpdateSlotButton(!hasRole(group1, roles));
    }, [appointment]);

    useEffect(() => {
        setIsUpdateButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    useEffect(() => {
        if (successMessage === `Appointment slot ${newEntry.type} has been updated (${hash})`) {
            setIsUpdateButtonDisabled(false);
            if (selectedParticipants.length < 1) onPanelClose();
        }
        if (successMessage.includes(`Appointment slot ${newEntry.type} has been deleted`)) {
            setIsUpdateButtonDisabled(false);
            onPanelClose();
        }
    }, [successMessage]);

    useEffect(() => {
        if (errorMessage) {
            setIsUpdateButtonDisabled(false);
        }
    }, [errorMessage]);

    // HELPER FNS
    const clearError = (key) => {
        if (key in errors && errors[key].error)
            setErrors((prev) => ({ ...prev, [key]: { error: false, message: '' } }));
    };

    const configTitleRow = () => {
        return (
            <>
                {newEntry.service}
                <span>
                    {reservedAppointments.length} / {newEntry.numberOfSlots}
                </span>
            </>
        );
    };

    const addUserEmail = () => users[appointment.userId]?.loginEmailAddress || '';

    // EVENT HANDLERS
    const onPanelClose = () => {
        setNewEntry(initialState);
        dispatch(setParticipantsCurrentAppointment({}));
        dispatch(setOpenAppointmentSchedulerPanel(false));
        dispatch(clearNewAppointment());
    };

    const onAuditModalClose = (e, reason) => {
        e.stopPropagation();
        if (reason === 'backdropClick') return;
        setShowAuditModal(false);
        setAuditLabel('');
        setAuditModalHeadingLabel('');
        dispatch(clearAppointmentAuditData());
    };

    const onUpdate = (key, value) => {
        clearError(key);
        setIsDisabledStartSlotButton(true);
        setIsDisabledUpdateSlotButton(true);
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onUpdateAppointmentType = (key, value, type) => {
        onUpdate(key, value);
        setNewEntry((prev) => ({ ...prev, type }));
    };

    const onUpdateLocation = (key, value, location) => {
        clearError(key);
        const additionalDetails =
            location?.name?.toLowerCase() !== 'community' ? '' : newEntry.additionalDetails;
        setNewEntry((prev) => ({
            ...prev,
            location: location?.name || '',
            additionalDetails,
            [key]: value
        }));
    };

    const onUpdateTime = (key, value) => {
        clearError(key === 'endTime' ? 'startTime' : 'endTime');
        onUpdate(key, value);
    };

    const onDelete = () => {
        dispatch(
            deleteParticipantCalendarAppointmentSlot(
                currentParticipant.id,
                newEntry.userId,
                newEntry.type,
                newEntry.id
            )
        );
    };

    const updateSelectedParticipants = (updatedSelectedParticipants) => {
        setIsDisabledStartSlotButton(true);
        setIsDisabledUpdateSlotButton(true);
        setSelectedParticipants(updatedSelectedParticipants);
    };

    const onSearchAuditItems = (label, modalHeadingLabel = '', participantId = '') => {
        if (newEntry.id) dispatch(loadAppointmentAuditData('appointment-slot', newEntry.id, label));
        setShowAuditModal(true);
        setAuditLabel(label);
        setAuditModalHeadingLabel(modalHeadingLabel ?? label);
        auditParticipantId !== participantId && setAuditParticipantId(participantId);
    };

    const onSubmit = (e) => {
        e.preventDefault();
        setIsUpdateButtonDisabled(true);
        const {
            /* eslint-disable no-unused-vars */
            type,
            service,
            location,
            ...rest
        } = newEntry;
        const participantIds = selectedParticipants.map((el) => el.id);
        const payload = { ...rest, participantIds };
        const referralDates = selectedParticipants.map((el) => el.referralDate);
        const newErrors = validate(payload, initialStateOnEntry, referralDates, roles);
        if (Object.values(newErrors).some((el) => el.error)) {
            setErrors(newErrors);
            return;
        }
        const hash = createHash();
        setHash(hash);
        dispatch(
            updateParticipantCalendarAppointmentSlot(
                currentParticipant.id,
                newEntry.type,
                payload,
                hash
            )
        );
        setInitialStateOnEntry(payload);
        setNewEntry((prev) => ({
            ...prev,
            participantIds
        }));
    };

    // RENDER
    return (
        <>
            <Panel width="500px" open={openAppointmentSchedulerPanel} onToggle={onPanelClose}>
                <div className={sidePanel.panelActions}>
                    <Switch
                        id={'auditDisplayFields'}
                        label="Show Audit Fields"
                        onOff={true}
                        customClass="auditSwitch"
                        checked={isAuditable}
                        onChange={(e) => setIsAuditable(e.target.checked)}
                    />
                    <OnDeleteIcon
                        onDelete={onDelete}
                        roles={roles}
                        acceptedRoles={deleteRoles}
                        active={!!newEntry.id}
                    />
                </div>
                <div className={sidePanel.formWrapper}>
                    <form onSubmit={onSubmit} data-testid="participant_slots_edit_form">
                        <StaticField content={configTitleRow()} customClass="underlinedContent" />

                        <StaticField
                            id="teamId"
                            label="Team"
                            content={getNameFromId(allTeams, newEntry.teamId)}
                        />
                        {canEditSlot(areAnySlotsResolved, 'adviser', roles, isInPast) ? (
                            <AdviserSelect
                                userId={newEntry.userId || ''}
                                serviceId={newEntry.serviceId}
                                teamId={newEntry.teamId}
                                initialAdviser={appointmentAdviser}
                                onAdviserChange={(id) => onUpdate('userId', id)}
                                disabled={!newEntry.service || !hasRole(acceptedRoles, roles)}
                                mandatory={true}
                                errors={errors}
                            />
                        ) : (
                            <StaticField id="adviserId" label="Adviser" content={addUserEmail()} />
                        )}

                        {canEditSlot(areAnySlotsResolved, 'appointmentType', roles, isInPast) ? (
                            <AppointmentTypeSelect
                                appointmentTypes={
                                    appointmentTypesByContractId[currentParticipant.contractId]
                                }
                                appointmentTypeId={newEntry.typeId}
                                onAppointmentTypeChange={onUpdateAppointmentType}
                                errors={errors}
                            />
                        ) : (
                            <StaticField
                                id="type"
                                label="Appointment Type"
                                content={newEntry.type}
                            />
                        )}

                        {canEditSlot(areAnySlotsResolved, 'location', roles, isInPast) ? (
                            <>
                                <LocationSelect
                                    locationId={newEntry.locationId}
                                    serviceId={newEntry.serviceId}
                                    onLocationChange={onUpdateLocation}
                                    errors={errors}
                                    isAuditable={isAuditable}
                                    onSearchAuditItems={onSearchAuditItems}
                                />
                                {newEntry?.location?.toLowerCase() === 'community' && (
                                    <TextInputField
                                        id="additionalDetails"
                                        label="Additional Details"
                                        placeholder="Enter additional details..."
                                        maxLength={ADDITIONAL_DETAILS_LIMIT}
                                        value={newEntry.additionalDetails || ''}
                                        onChange={(e) =>
                                            onUpdate('additionalDetails', e.target.value)
                                        }
                                    />
                                )}
                            </>
                        ) : (
                            <>
                                <StaticField
                                    id="location"
                                    label="Location"
                                    content={newEntry.location}
                                    isAuditable={isAuditable}
                                    onSearchAuditItems={onSearchAuditItems}
                                />
                                {newEntry.additionalDetails && (
                                    <StaticField
                                        id="additionalDetails"
                                        label="Additional Details"
                                        content={newEntry.additionalDetails}
                                    />
                                )}
                            </>
                        )}

                        {canEditSlot(areAnySlotsResolved, 'startAndEndTime', roles, isInPast) ? (
                            <StartAndEndTimesSelect
                                startTime={newEntry.startTime}
                                endTime={newEntry.endTime}
                                onTimeChange={onUpdateTime}
                                errors={errors}
                                isAuditable={isAuditable}
                                onSearchAuditItems={onSearchAuditItems}
                            />
                        ) : (
                            <>
                                <StaticField
                                    id="startTime"
                                    label="Start Time"
                                    content={newEntry.startTime}
                                    isAuditable={isAuditable}
                                    onSearchAuditItems={() =>
                                        onSearchAuditItems('Start Date', 'Start Time')
                                    }
                                />
                                <StaticField
                                    id="endTime"
                                    label="End Time"
                                    content={newEntry.endTime}
                                    isAuditable={isAuditable}
                                    onSearchAuditItems={() =>
                                        onSearchAuditItems('End Date', 'End Time')
                                    }
                                />
                            </>
                        )}

                        {canEditSlot(areAnySlotsResolved, 'date', roles, isInPast) ? (
                            <DateSelect
                                mandatory={true}
                                value={newEntry.date}
                                error={errors.date}
                                onDateChange={(date) => onUpdate('date', date)}
                                isAuditable={isAuditable}
                                onSearchAuditItems={() => onSearchAuditItems('Start Date', 'Date')}
                            />
                        ) : (
                            <StaticField
                                id="date"
                                label="Date"
                                content={reverseFormatDate(newEntry.date)}
                                isAuditable={isAuditable}
                                onSearchAuditItems={() => onSearchAuditItems('Start Date', 'Date')}
                            />
                        )}

                        <ParticipantsSelect
                            serviceId={currentEvent.serviceId}
                            selectedParticipants={selectedParticipants}
                            updateSelectedParticipants={updateSelectedParticipants}
                            numOfSlots={newEntry.numberOfSlots}
                            statuses={statuses}
                            reservedAppointments={reservedAppointments}
                            roles={roles}
                            areAnySlotsResolved={areAnySlotsResolved}
                            isInPast={isInPast}
                            appointmentDate={newEntry.date}
                            hasHiddenParticipants={true}
                        />

                        <div className={sidePanel.updateSlotsButton}>
                            <Button
                                id="appointmentSlotEditButton"
                                content="Update"
                                disabled={
                                    !canEditSlot(
                                        areAnySlotsResolved,
                                        'updateButton',
                                        roles,
                                        isInPast
                                    ) || isUpdateButtonDisabled
                                }
                                error={errors.button}
                                clearError={() => clearError('button')}
                                customErrorClass="rightSideButtonError"
                            />
                        </div>
                    </form>

                    <StartAppointmentSlotsRows
                        participants={selectedParticipants}
                        appointment={newEntry}
                        disabled={isDisabledStartSlotButton}
                        isUpdateDisabled={isDisabledUpdateSlotButton}
                        hasParticipantAccess={hasParticipantAccess}
                        onPanelClose={onPanelClose}
                        statuses={statuses}
                        isAuditable={isAuditable}
                        onSearchAuditItems={onSearchAuditItems}
                    />
                </div>
            </Panel>
            {showAuditModal && (
                <AuditAppointmentModal
                    isOpen={showAuditModal}
                    label={auditLabel}
                    modalHeadingLabel={auditModalHeadingLabel}
                    participantId={auditParticipantId}
                    onClose={onAuditModalClose}
                />
            )}
        </>
    );
};

ParticipantAppointmentSlotsEdit.propTypes = {
    currentEvent: PropTypes.object
};

export default ParticipantAppointmentSlotsEdit;
